// Pure helpers for storefront deep links (dunea.ai marketplace pages link into
// the app) and for resuming a paid template remix after Stripe Checkout. No
// React/store imports.
import type { RemixView } from '@dunea/shared';

export type StorefrontIntent =
  | { kind: 'remix'; slug: string }
  | { kind: 'listing'; slug: string }
  | { kind: 'expert'; handle: string }
  | { kind: 'wishlist'; wishId: string | null };

export interface StorefrontParams {
  intent: StorefrontIntent | null;
  ref: string | null;
  /** Query keys this module handled (and the caller should strip). */
  handled: string[];
}

const SLUG = /^[a-z][a-z0-9-]{2,40}$/;
const HANDLE = /^[a-z][a-z0-9-]{2,39}$/;
const WISH_ID = /^[A-Za-z0-9_-]{1,64}$/;
const REF = /^[A-Za-z0-9_-]{2,64}$/;

/** `?marketplace=success|cancel` is the Stripe return marker, not a listing slug. */
const RETURN_MARKERS = new Set(['success', 'cancel', 'cancelled']);

/**
 * Read `?marketplace=<slug>`, `?remix=<slug>`, `?expert=<handle>`,
 * `?wishlist=1`, `?wish=<id>` and `&ref=<code>`. One intent wins, in that
 * order of priority: remix, marketplace, expert, wish, wishlist. Invalid
 * values are ignored (and left in the URL).
 */
export function parseStorefrontParams(search: string): StorefrontParams {
  const params = new URLSearchParams(search);
  const handled: string[] = [];
  const take = (key: string, re: RegExp): string | null => {
    const value = params.get(key);

    if (value !== null && re.test(value)) {
      handled.push(key);
      return value;
    }

    return null;
  };

  const ref = take('ref', REF);
  const remix = take('remix', SLUG);
  const marketplaceRaw = params.get('marketplace');
  const marketplace = marketplaceRaw && !RETURN_MARKERS.has(marketplaceRaw) ? take('marketplace', SLUG) : null;
  const expert = take('expert', HANDLE);
  const wish = take('wish', WISH_ID);
  const wishlistRaw = params.get('wishlist');
  const wishlist = wishlistRaw === '1' || wishlistRaw === 'true';

  if (wishlist) {
    handled.push('wishlist');
  }

  const intent: StorefrontIntent | null = remix
    ? { kind: 'remix', slug: remix }
    : marketplace
      ? { kind: 'listing', slug: marketplace }
      : expert
        ? { kind: 'expert', handle: expert }
        : wish
          ? { kind: 'wishlist', wishId: wish }
          : wishlist
            ? { kind: 'wishlist', wishId: null }
            : null;

  return { intent, ref, handled };
}

/** `href` without these query keys (for history.replaceState). */
export function withoutParams(href: string, keys: readonly string[]): string {
  const url = new URL(href);

  for (const key of keys) {
    url.searchParams.delete(key);
  }

  return url.toString();
}

// ─── stash across the sign-in hop ───

export const INTENT_TTL_MS = 60 * 60 * 1000;

export function serializeIntent(intent: StorefrontIntent, now = Date.now()): string {
  return JSON.stringify({ intent, at: now });
}

/** A stashed intent, or null when missing, malformed or older than the TTL. */
export function readIntent(raw: string | null | undefined, now = Date.now()): StorefrontIntent | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as { intent?: StorefrontIntent; at?: number };
    const intent = parsed.intent;

    if (!intent || typeof parsed.at !== 'number' || now - parsed.at > INTENT_TTL_MS || now < parsed.at - 60_000) {
      return null;
    }

    switch (intent.kind) {
      case 'remix':
      case 'listing':
        return SLUG.test(intent.slug) ? intent : null;
      case 'expert':
        return HANDLE.test(intent.handle) ? intent : null;
      case 'wishlist':
        return intent.wishId === null || WISH_ID.test(intent.wishId) ? intent : null;
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/** What the Marketplace panel should open for a non-remix intent. */
export function panelTarget(intent: Exclude<StorefrontIntent, { kind: 'remix' }>): {
  view: 'plugins' | 'experts' | 'wishlist';
  slug: string | null;
  expert: string | null;
  wishId: string | null;
} {
  if (intent.kind === 'listing') {
    return { view: 'plugins', slug: intent.slug, expert: null, wishId: null };
  }

  if (intent.kind === 'expert') {
    return { view: 'experts', slug: null, expert: intent.handle, wishId: null };
  }

  return { view: 'wishlist', slug: null, expert: null, wishId: intent.wishId };
}

/** The chat to land on: most recently updated first. */
export function mostRecentChat<T extends { id: string; urlId?: string | null; updatedAt: string }>(
  chats: readonly T[],
): T | null {
  let best: T | null = null;

  for (const c of chats) {
    if (!best || (Date.parse(c.updatedAt) || 0) > (Date.parse(best.updatedAt) || 0)) {
      best = c;
    }
  }

  return best;
}

// ─── paid remix resume ───

export const REMIX_RESUME_WINDOW_MS = 15 * 60 * 1000;

/**
 * The remix Checkout just paid for: the newest one, if it's created, has a
 * chat, and was made within the window. Optionally it must be for `slug`.
 */
export function pickResumableRemix(
  remixes: readonly RemixView[] | null | undefined,
  now = Date.now(),
  slug?: string | null,
  windowMs = REMIX_RESUME_WINDOW_MS,
): (RemixView & { chatId: string }) | null {
  const newest = (remixes ?? [])[0];

  if (!newest || newest.status !== 'created' || !newest.chatId) {
    return null;
  }

  if (slug && newest.listingSlug && newest.listingSlug !== slug) {
    return null;
  }

  const at = Date.parse(newest.createdAt);

  if (!Number.isFinite(at) || now - at > windowMs) {
    return null;
  }

  return newest as RemixView & { chatId: string };
}

export const PENDING_REMIX_TTL_MS = 2 * 60 * 60 * 1000;

/** The slug of a template whose Checkout we left for, if it's recent. */
export function readPendingRemix(raw: string | null | undefined, now = Date.now()): string | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as { slug?: unknown; at?: unknown };

    return typeof parsed.slug === 'string' &&
      SLUG.test(parsed.slug) &&
      typeof parsed.at === 'number' &&
      now - parsed.at <= PENDING_REMIX_TTL_MS
      ? parsed.slug
      : null;
  } catch {
    return null;
  }
}

/**
 * After `?marketplace=success`: resume a remix when we left for a template's
 * Checkout, or when the returned install is a template (or isn't a known
 * install at all). A plugin, theme or skill purchase never resumes a remix.
 */
export function shouldResumeRemix(input: {
  pendingSlug: string | null;
  installId: string | null;
  installs: readonly { id: string; kind: string }[] | null | undefined;
}): boolean {
  if (input.pendingSlug) {
    return true;
  }

  const match = input.installId ? (input.installs ?? []).find((i) => i.id === input.installId) : null;

  return match ? match.kind === 'template' : true;
}
